import { useState } from "react";
import type { CmmdBoardTask, CmmdMember, CmmdProject } from "@cmmd-forge/contracts";
import { HashIcon, MegaphoneIcon } from "lucide-react";

import type { SidebarThreadSummary } from "~/types";
import { SettingsPageContainer, SettingsRow, SettingsSection } from "../settings/settingsLayout";
import { ProjectTaskSummary } from "../tasks/ProjectTaskSummary";
import {
  ProjectDisplayNameControl,
  ProjectIconControl,
  ProjectLeadControl,
  ProjectMembersControl,
  ProjectNameControl,
  ProjectPriorityControl,
  ProjectStatusControl,
  ProjectTargetDateControl,
} from "./CmmdProjectSettingsControls";
import type { ForgeThreadTarget } from "./forgeExecutionTarget";
import { CmmdProjectDangerSection } from "./CmmdProjectDangerSection";
import type { ProjectExecutionStatus } from "./projectExecutionStatus";
import type { ForgeProjectRemovalTarget } from "./CmmdProjectDeleteControl";
import { ProjectActiveWorkSummary } from "./ProjectActiveWorkSummary";
import {
  ProjectRepositoryValue,
  type ProjectRepositoryBindingState,
} from "./ProjectRepositoryValue";
import { ProjectWorkItemPrefixValue } from "./ProjectWorkItemPrefixValue";
import { useProjectWorkItemPrefixUpdate } from "./ProjectWorkItemPrefixContext";
import type { CmmdProjectSettingsPatch } from "./projectSettingsPatch";
import { resolveProjectLeadAvatarUrl } from "./resolveProjectLeadAvatar";
import type { ProjectWorkSessionsState } from "./useProjectWorkSessions";

export interface CmmdProjectSettingsTabProps {
  readonly project: CmmdProject;
  readonly members: readonly CmmdMember[];
  readonly tasks: readonly CmmdBoardTask[];
  readonly threadSummaries: readonly SidebarThreadSummary[];
  readonly tasksLoading: boolean;
  readonly workSessions: ProjectWorkSessionsState;
  readonly bindingState: ProjectRepositoryBindingState;
  /**
   * The project's Forge-link state, resolved once by `projectExecutionStatus`.
   *
   * Passed in rather than re-derived from `forgeRemovalTargets.length`: that
   * count says whether this SESSION can remove a Forge project, not whether one
   * EXISTS, and answering the second with the first told every unreachable and
   * every failed-to-check project that nothing was connected.
   */
  readonly executionStatus: ProjectExecutionStatus;
  readonly onUpdateSettings: (patch: CmmdProjectSettingsPatch) => Promise<void>;
  readonly onUpdateRepository: (githubRepoUrl: string) => Promise<void>;
  readonly onOpenWorkSessionThread: (target: ForgeThreadTarget) => void;
  readonly onManageMembers: () => void;
  readonly forgeRemovalTargets: readonly ForgeProjectRemovalTarget[];
  readonly onForgeProjectDeleted: () => Promise<void>;
  readonly onRefresh: () => Promise<void>;
}

/**
 * Everything about the project that is configuration rather than work.
 *
 * This replaces the details rail that used to sit to the right of the board:
 * the same properties, given room to become editable, plus the project-level
 * actions (rename, archive) that had nowhere to live before.
 */
export function CmmdProjectSettingsTab(props: CmmdProjectSettingsTabProps) {
  const { project } = props;
  const updateWorkItemPrefix = useProjectWorkItemPrefixUpdate();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const leadAvatarUrl = resolveProjectLeadAvatarUrl(project, props.members);

  const save = (patch: CmmdProjectSettingsPatch) => {
    setSaving(true);
    setError(null);
    void props
      .onUpdateSettings(patch)
      .catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : "Could not save the project."),
      )
      .finally(() => setSaving(false));
  };

  return (
    <SettingsPageContainer>
      {error !== null ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}

      <SettingsSection title="Project">
        {/* Two names, deliberately. A portfolio board and a working sidebar want
            different things from the same project, and this is where that is
            said out loud — the alternative is one field that quietly renames
            both, which is what these rows replaced. */}
        <SettingsRow
          title="Name"
          description="Shown across Forge. Empty uses the board name."
          control={
            <ProjectDisplayNameControl
              shortName={project.shortName ?? null}
              title={project.title}
              disabled={saving}
              onSave={save}
            />
          }
        />
        <SettingsRow
          title="Board name"
          description="Shown on the CMMD board and to agents."
          control={<ProjectNameControl title={project.title} disabled={saving} onSave={save} />}
        />
        <SettingsRow
          title="Icon"
          description={
            project.iconUrl
              ? "Currently synced from the linked repository. Choosing an emoji replaces it."
              : "Shown wherever this project appears. Leave empty to use the linked repository's icon."
          }
          control={
            <ProjectIconControl icon={project.icon ?? null} disabled={saving} onSave={save} />
          }
        />
        <SettingsRow
          title="Status"
          description="Where the project stands. Drives its column on the Projects board."
          control={<ProjectStatusControl status={project.status} disabled={saving} onSave={save} />}
        />
        <SettingsRow
          title="Priority"
          description="How this project ranks against the rest of the portfolio."
          control={
            <ProjectPriorityControl priority={project.priority} disabled={saving} onSave={save} />
          }
        />
        <SettingsRow
          title="Lead"
          description="The person accountable for the project. Receives Project Lead briefings."
          control={
            <ProjectLeadControl
              ownerId={project.ownerId}
              ownerName={project.ownerName}
              ownerAvatarUrl={leadAvatarUrl}
              members={props.members}
              disabled={saving}
              onSave={save}
            />
          }
        />
        <SettingsRow
          title="Target date"
          description="The date this project is aiming to land. Shown on the timeline."
          control={
            <ProjectTargetDateControl
              targetEndDate={project.targetEndDate}
              disabled={saving}
              onSave={save}
            />
          }
        />
        <SettingsRow
          title="Members"
          description="People and agents with access to this project."
          control={
            <ProjectMembersControl
              members={props.members}
              onManageMembers={props.onManageMembers}
            />
          }
        />
        {project.campaignTitle ? (
          <SettingsRow
            title="Campaign"
            description="The CMMD campaign this project rolls up to. Changed in CMMD."
            control={
              <span className="inline-flex min-w-0 items-center gap-1.5 text-sm text-foreground">
                <MegaphoneIcon className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
                <span className="truncate">{project.campaignTitle}</span>
              </span>
            }
          />
        ) : null}
      </SettingsSection>

      <SettingsSection title="Work">
        <SettingsRow
          title="Task key"
          description="The namespace every work item in this project is numbered under, such as FOR-1412."
          control={
            <span className="inline-flex min-w-0 items-center gap-1.5">
              <HashIcon className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
              <ProjectWorkItemPrefixValue
                value={project.workItemPrefix}
                onSave={updateWorkItemPrefix}
              />
            </span>
          }
        />
        <SettingsRow
          title="Repository"
          description="The GitHub repository agents check out when they start work in this project."
          control={
            <ProjectRepositoryValue
              githubRepoUrl={project.githubRepoUrl}
              bindingState={props.bindingState}
              workspaceId={project.workspaceId}
              onSave={props.onUpdateRepository}
            />
          }
        />
      </SettingsSection>

      <CmmdProjectSettingsProgress
        tasks={props.tasks}
        threadSummaries={props.threadSummaries}
        tasksLoading={props.tasksLoading}
        workSessions={props.workSessions}
        onOpenWorkSessionThread={props.onOpenWorkSessionThread}
      />

      <CmmdProjectDangerSection
        project={project}
        threadSummaries={props.threadSummaries}
        executionStatus={props.executionStatus}
        forgeRemovalTargets={props.forgeRemovalTargets}
        onForgeProjectDeleted={props.onForgeProjectDeleted}
        onRefresh={props.onRefresh}
      />
    </SettingsPageContainer>
  );
}

interface CmmdProjectSettingsProgressProps {
  readonly tasks: readonly CmmdBoardTask[];
  readonly threadSummaries: readonly SidebarThreadSummary[];
  readonly tasksLoading: boolean;
  readonly workSessions: ProjectWorkSessionsState;
  readonly onOpenWorkSessionThread: (target: ForgeThreadTarget) => void;
}

/**
 * Read-only carryover from the details rail: how the board is tracking and what
 * agents are doing right now. Kept next to the settings it explains rather than
 * dropped when the rail went away.
 */
function CmmdProjectSettingsProgress(props: CmmdProjectSettingsProgressProps) {
  const showActiveWork =
    props.workSessions.loading ||
    props.workSessions.error != null ||
    props.workSessions.sessions.length > 0;

  return (
    <>
      <SettingsSection title="Progress">
        <div className="px-4 py-4 sm:px-5">
          <ProjectTaskSummary
            density="overview"
            tasks={props.tasks}
            threadSummaries={props.threadSummaries}
            loading={props.tasksLoading}
          />
        </div>
      </SettingsSection>

      {showActiveWork ? (
        <SettingsSection title="Active work">
          <div className="px-4 py-4 sm:px-5">
            <ProjectActiveWorkSummary
              sessions={props.workSessions.sessions}
              tasks={props.tasks}
              loading={props.workSessions.loading}
              error={props.workSessions.error}
              onOpenThread={props.onOpenWorkSessionThread}
            />
          </div>
        </SettingsSection>
      ) : null}
    </>
  );
}
